import React, { Fragment, useMemo, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FaWpforms } from 'react-icons/fa';
import { RxRotateCounterClockwise } from 'react-icons/rx';
import { Button } from 'react-bootstrap';
import { VscLoading } from 'react-icons/vsc';
import { useHistory } from 'react-router-dom';
import LogFormatPdfStore from './LogFormatPdfStore';
import {
    Container,
    Table,
    Cell,
    Row,
    CellText,
    StickyCell,
    TableTitle,
    Filters,
    ToggleButton,
    HeaderCell,
    PAGE_HEIGHT,
    PAGE_WIDTH,
    MAX_DIMENSION,
    IMAGE_QUALITY,
} from './AccumulationViewStyles';
import { toast } from 'react-toastify';
import { getChannelIdFromSettingsURL } from '@/_helpers';

const getScaledDimensions = (width, height) => {
    const ratio = PAGE_WIDTH / width;
    return {
        width: PAGE_WIDTH,
        height: height * ratio,
        ratio,
    };
};
const splitCanvasToPages = (canvas, pageHeight) => {
    const pages = [];
    const totalHeight = canvas.height;
    const pageWidth = canvas.width;

    let yOffset = 0;

    while (yOffset < totalHeight) {
        const canvasPage = document.createElement('canvas');
        const pageCanvasHeight = Math.min(pageHeight, totalHeight - yOffset);
        canvasPage.width = pageWidth;
        canvasPage.height = pageCanvasHeight;

        const pageCtx = canvasPage.getContext('2d');
        pageCtx.drawImage(canvas, 0, yOffset, pageWidth, pageCanvasHeight, 0, 0, pageWidth, pageCanvasHeight);

        pages.push(canvasPage);
        yOffset += pageCanvasHeight;
    }

    return pages;
};

const SubmissionTable = React.memo(({ layout, table, submittedBy }) => {
    const fieldKeys = table.fields || [];
    const rows = table.data || [];
    const STICKY_COLUMNS_COUNT = 3;

    const CellContent = ({ cell, bold }) => {
        return (
            <Fragment>
                {cell?.image ? (
                    <img
                        src={`${cell?.image}`}
                        // src={`${cell?.image + '?noCache=' + Math.random().toString()}`}
                        crossOrigin="anonymous"
                        alt="upload"
                        width={100}
                        height={40}
                        fetchpriority="high"
                    />
                ) : (
                    <CellText bold={bold}>{cell?.value}</CellText>
                )}
            </Fragment>
        );
    };

    if (layout === 'vertical') {
        return (
            <Fragment>
                <TableTitle>
                    <FaWpforms />
                    {table?.formName || ''}
                </TableTitle>
                <Container>
                    <Table>
                        {/* Header row */}
                        <Row>
                            {fieldKeys.map((field, index) =>
                                index <= 2 ? (
                                    submittedBy && index === 2 ? null : (
                                        <StickyCell key={index} left={120 * index} zIndex={10 - index} sticky header>
                                            {field?.fieldName}
                                        </StickyCell>
                                    )
                                ) : (
                                    <HeaderCell key={index} width={200}>
                                        {field?.fieldName}
                                    </HeaderCell>
                                )
                            )}
                        </Row>

                        {/* Data rows */}
                        {rows.map((row, rowIndex) => (
                            <Row key={rowIndex}>
                                {row.map((cell, cellIndex) =>
                                    cellIndex < STICKY_COLUMNS_COUNT ? (
                                        submittedBy && cellIndex === 2 ? null : (
                                            <StickyCell key={cellIndex} left={120 * cellIndex} sticky>
                                                <CellContent cell={cell} bold={true} />
                                            </StickyCell>
                                        )
                                    ) : (
                                        <Cell key={cellIndex}>
                                            <CellContent cell={cell} />
                                        </Cell>
                                    )
                                )}
                            </Row>
                        ))}
                    </Table>
                </Container>
            </Fragment>
        );
    } else {
        return (
            <Fragment>
                <TableTitle>
                    <FaWpforms />
                    {table?.formName || ''}
                </TableTitle>
                <Container>
                    <Table>
                        {/* Each field becomes a row */}
                        {fieldKeys.map((field, fieldIndex) => (
                            <Row key={fieldIndex}>
                                {submittedBy && fieldIndex === 2 ? null : (
                                    <StickyCell left={0} width={200} sticky>
                                        {field?.fieldName}
                                    </StickyCell>
                                )}

                                {rows.map((row, rowIndex) =>
                                    submittedBy && fieldIndex === 2 ? null : (
                                        <Cell key={rowIndex} width={200} highlight={fieldIndex <= 2}>
                                            <CellContent cell={row[fieldIndex]} bold={fieldIndex <= 2} />
                                        </Cell>
                                    )
                                )}
                            </Row>
                        ))}
                    </Table>
                </Container>
            </Fragment>
        );
    }
});

const SubmissionViewer = React.memo(({ tables = [], user, teamInfo }) => {
    const history = useHistory();
    const channelId = getChannelIdFromSettingsURL();

    const exportRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [layout, setLayout] = useState('vertical');
    const submittedBy = useMemo(() => {
        return user && user?.label;
    }, [user]);

    const handleToggleLayout = () => {
        setLayout(layout === 'vertical' ? 'horizontal' : 'vertical');
    };

    const handleDownload = async ({ formBuilder }) => {
        setIsDownloading(true);
        const original = exportRef.current;
        const clone = original.cloneNode(true);
        clone.style.overflow = 'visible';
        clone.style.width = 'auto';
        clone.style.position = 'absolute';
        clone.style.left = '-9999px';
        clone.style.top = '0';
        const elementsToRemove = clone.querySelectorAll('video, audio, iframe, object, embed');
        elementsToRemove.forEach((el) => el.remove());
        const stickyCells = clone.querySelectorAll('[data-sticky-cell]');
        stickyCells.forEach((cell) => {
            cell.style.position = 'relative';
            cell.style.left = 'auto';
            cell.style.zIndex = 'auto';
        });

        document.body.appendChild(clone);

        try {
            // Add delay to ensure DOM is ready for rendering
            await new Promise((resolve) => setTimeout(resolve, 3000));

            const originalWidth = clone.scrollWidth;
            const originalHeight = clone.scrollHeight;

            // Auto scale to fit max canvas size
            let canvasScale = 1;
            if (originalWidth > MAX_DIMENSION || originalHeight > MAX_DIMENSION) {
                canvasScale = Math.min(MAX_DIMENSION / originalWidth, MAX_DIMENSION / originalHeight);
                if (layout === 'vertical') {
                    canvasScale = Math.max(1, canvasScale);
                }
            }

            const canvas = await html2canvas(clone, {
                scale: canvasScale,
                useCORS: true,
                scrollX: 0,
                scrollY: 0,
                windowWidth: originalWidth,
                windowHeight: originalHeight,
                onclone: (clonedDoc) => {
                    const style = clonedDoc.createElement('style');
                    style.textContent = `
                @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');
                * {
                    font-family: 'Lato', sans-serif !important;
                }
              `;
                    clonedDoc.head.appendChild(style);
                },
            });

            if (!canvas || canvas.width === 0 || canvas.height === 0) {
                throw new Error('Canvas rendering failed - empty result');
            }

            const { width: scaledW, height: scaledH, ratio } = getScaledDimensions(canvas.width, canvas.height);
            const pages = splitCanvasToPages(canvas, PAGE_HEIGHT / ratio);
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'px',
                format: [scaledW, PAGE_HEIGHT],
            });
            for (let i = 0; i < pages.length; i++) {
                const pageCanvas = pages[i];

                if (!pageCanvas || pageCanvas.width === 0 || pageCanvas.height === 0) {
                    console.warn(`Skipping empty page ${i}`);
                    continue;
                }

                await new Promise((resolve) => setTimeout(resolve, 100));

                const imgData = pageCanvas.toDataURL('image/png', IMAGE_QUALITY);

                if (i > 0) {
                    pdf.addPage([scaledW, PAGE_HEIGHT]);
                }

                const finalHeight = pageCanvas.height * ratio;

                pdf.addImage(imgData, 'PNG', 0, 0, scaledW, finalHeight, undefined, 'SLOW');
            }

            if (formBuilder) {
                pdf.addPage([scaledW, PAGE_HEIGHT]);
                const blob = pdf.output('blob');
                const accumulationPdfId = Date.now().toString();
                const metadata = {
                    pages: pdf.internal.getNumberOfPages(),
                };

                await LogFormatPdfStore.save(accumulationPdfId, blob, metadata);
                const pdfOutput = pdf.output('arraybuffer');

                if (pdfOutput.byteLength > 20 * 1024 * 1024) {
                    throw new Error('Generated PDF exceeds 20MB limit. Please reduce content size.');
                }

                return accumulationPdfId;
            } else {
                pdf.save('submissions.pdf');
            }
        } catch (error) {
            console.error('PDF generation failed:', error);
            toast.error(error?.message || 'Failed to generate PDF. Please try again');
        } finally {
            if (clone.parentNode === document.body) {
                document.body.removeChild(clone);
            }
            setIsDownloading(false);
        }
    };
    const handlePrepareForSubmission = async () => {
        if (channelId) {
            return toast.error('Not allowed in channel settings');
        }
        const accumulationPdfId = await handleDownload({ formBuilder: true });
        if (accumulationPdfId) {
            history.push(`/form-builder?accumulationPdfId=${accumulationPdfId}`);
        }
    };
    if (!tables?.length) {
        return <div className="text-center">No Matching Results</div>;
    }

    return (
        <Fragment>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <ToggleButton onClick={handleToggleLayout}>
                        <RxRotateCounterClockwise size={24} className="mr-2" />
                        Rotate {layout === 'vertical' ? 'Horizontal' : 'Vertical'}
                    </ToggleButton>
                </div>
                <div className="d-flex justify-content-between align-items-center ">
                    <div>
                        <Button className="ml-2" onClick={handlePrepareForSubmission} disabled={isDownloading}>
                            {isDownloading && <VscLoading size={24} className="spin mr-2" />}
                            Prepare For Submission
                        </Button>
                    </div>
                    <div>
                        <Button onClick={handleDownload} disabled={isDownloading} className="ml-4">
                            {isDownloading && <VscLoading size={24} className="spin mr-2" />}
                            {isDownloading ? 'Generating PDF...' : 'Download PDF'}
                        </Button>
                    </div>
                </div>
            </div>

            <div ref={exportRef} style={{ padding: '20px' }}>
                <Filters>
                    <p>Provider Name: {teamInfo?.name}</p>
                    <p>{submittedBy ? <span>Submitted By: {submittedBy} </span> : null}</p>
                </Filters>
                {tables.map((table, index) => (
                    <SubmissionTable key={index} table={table} layout={layout} submittedBy={submittedBy} />
                ))}
            </div>
        </Fragment>
    );
});

export default SubmissionViewer;
